import { hasWriteAccessToAllDepartments } from '@/helpers/departmentPermissions.ts';
import { ExtendedUserDTO } from '@/hooks/useGetAllAdmins';
import { checkUserPermissions } from '../Auth/checkUserPermissions';
import { FormattedMessage, useIntl } from 'react-intl';
import { DeleteConfirmationForm } from '../DeleteConfirmationForm';
import {
  getGetDepartmentUserWithDepartmentsQueryKey,
  getGetUsersForDepartmentQueryKey,
  useDeleteDepartmentUser,
  useDeleteUserProfileByID,
} from '@/api/trafikportalen/trafikportalenAPI';
import { useCallback } from 'react';
import { toast } from '@/hooks/useToast';
import { useNavigate } from '@tanstack/react-router';
import { useTenantID } from '@/hooks/useTenantID';
import { useQueryClient } from '@tanstack/react-query';
import { refreshDepartmentAccess, useAuth } from '@/stores/auth';

interface Props {
  administrator: ExtendedUserDTO;
}

export const AdministratorAdvancedForm = ({ administrator }: Props) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const tenantId = useTenantID();
  const queryClient = useQueryClient();
  const currentUserProfile = useAuth((state) => state.userProfile);

  const deleteAdministrator = useDeleteUserProfileByID();
  const deleteDepartmentUser = useDeleteDepartmentUser();

  const isDeletingSelf = administrator.email === currentUserProfile?.email;

  const onDelete = useCallback(async () => {
    try {
      if (
        administrator.adminType.startsWith('org') &&
        checkUserPermissions({ permission: 'organization:editor' })
      ) {
        await deleteAdministrator.mutateAsync({
          id: administrator.user_profiles[0].id,
        });
      }
      const departments = administrator.departments ?? [];
      await Promise.all(
        departments.map((department) =>
          deleteDepartmentUser.mutateAsync({
            departmentID: department.department_id,
            userID: administrator.id,
          }),
        ),
      );

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getGetDepartmentUserWithDepartmentsQueryKey(
            administrator.id,
          ),
        }),
        ...departments.map((department) =>
          queryClient.invalidateQueries({
            queryKey: getGetUsersForDepartmentQueryKey(
              department.department_id,
            ),
          }),
        ),
      ]);

      await refreshDepartmentAccess();

      toast.success(
        {
          title: formatMessage({ defaultMessage: 'Administratören raderades' }),
        },
        { autoClose: 3000 },
      );

      navigate({
        to: '/$tenantId/administrators',
        params: { tenantId },
      });
    } catch (error) {
      console.error('Error: ', error);
      toast.error(
        {
          title: formatMessage({ defaultMessage: 'Ett fel inträffade' }),
        },
        { autoClose: 3000 },
      );
    }
  }, [
    administrator.adminType,
    administrator.departments,
    administrator.id,
    administrator.user_profiles,
    queryClient,
    formatMessage,
    navigate,
    tenantId,
    deleteAdministrator,
    deleteDepartmentUser,
  ]);

  if (
    !checkUserPermissions({ permission: 'organization:editor' }) &&
    !hasWriteAccessToAllDepartments(administrator)
  ) {
    return (
      <div className="flex flex-col gap-4">
        <i>
          <FormattedMessage defaultMessage="Du har inte rättighet att ta bort denna administratören." />
        </i>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <DeleteConfirmationForm
        title={formatMessage({ defaultMessage: 'Radera' })}
        warningMessage={
          isDeletingSelf ? (
            <FormattedMessage defaultMessage="Du kan inte ta bort dig själv." />
          ) : (
            <FormattedMessage
              defaultMessage="All data för {name} kommer att raderas och kan inte ångras. Det kan ta upp till 10 minuter innan användaren blir utloggad och inte längre kan logga in."
              values={{
                name: `${administrator.firstname} ${administrator.lastname}`,
              }}
            />
          )
        }
        canDelete={!isDeletingSelf}
        onDelete={onDelete}
      />
    </div>
  );
};
